import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore, track, telemetryEvents } from '@carrent/core'
import { PageHeader, StatusBadge, Icon, DueBadge } from '@carrent/ui'
import {
  useTasks,
  availableScopes,
  canCreateTask,
  priorityMeta,
  describeDue,
  matchesDueFilter,
  TASK_DUE_FILTERS,
  TASK_STATUS_FILTERS,
} from '@carrent/domain'

/**
 * Задачи в консоли — плотная таблица.
 *
 * Десктопная композиция: много колонок, сортировка, переход по строке.
 * Домен общий с водительским приложением, представление — своё.
 * Бриф прямо запрещает делать десктоп растянутой мобильной лентой.
 */
const COLUMNS = [
  { key: 'id', label: '#', sortable: true },
  { key: 'title', label: 'Задача', sortable: true },
  { key: 'status', label: 'Статус', sortable: true },
  { key: 'priority', label: 'Приоритет', sortable: true },
  { key: 'assignee', label: 'Исполнитель' },
  { key: 'car', label: 'Машина' },
  { key: 'due_at', label: 'Срок', sortable: true },
]

function sortValue(task, key) {
  switch (key) {
    case 'priority':
      return priorityMeta(task.priority).weight
    case 'due_at':
      return task.due_at ? new Date(task.due_at).getTime() : Infinity
    case 'title':
      return (task.title || '').toLowerCase()
    default:
      return task[key]
  }
}

export default function TasksPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState(() => searchParams.get('status') ?? '')
  const [scope, setScope] = useState('all')
  // Подборка по сроку приходит из URL: гаджет «просрочено»
  // на дашборде и раньше вёл сюда, но фильтр существовал только
  // как одноразовая проверка внутри сортировки.
  const [due, setDue] = useState(() => (searchParams.get('overdue') === '1' ? 'overdue' : ''))
  const [sort, setSort] = useState({ key: 'due_at', dir: 'asc' })

  const { tasks, loading, error, reload } = useTasks({ status, scope })
  const scopes = availableScopes(user)

  const sorted = useMemo(() => {
    const now = new Date()
    const rows = tasks.filter((t) => matchesDueFilter(t, due, now))
    rows.sort((a, b) => {
      const av = sortValue(a, sort.key)
      const bv = sortValue(b, sort.key)
      if (av === bv) return 0
      return (av > bv ? 1 : -1) * (sort.dir === 'asc' ? 1 : -1)
    })
    return rows
  }, [tasks, sort, due])

  const toggleSort = (key) => {
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))
    track(telemetryEvents.FILTER_APPLIED, { screen: 'tasks', filter_key: `sort:${key}` })
  }

  return (
    <div className="flex-col gap">
      <PageHeader
        title="Задачи"
        right={
          canCreateTask(user) ? (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => navigate('/admin/tasks/new')}
            >
              <Icon name="plus" size={14} />
              Создать
            </button>
          ) : undefined
        }
      />

      <div className="toolbar">
        <div className="chip-row">
          {TASK_STATUS_FILTERS.map((f) => (
            <button
              key={f.key || 'all'}
              type="button"
              className={`chip ${status === f.key ? 'chip--active' : ''}`}
              onClick={() => {
                setStatus(f.key)
                // Что фильтруют в консоли — основание для того,
                // чтобы неиспользуемые фильтры однажды убрать.
                track(telemetryEvents.FILTER_APPLIED, {
                  screen: 'tasks',
                  filter_key: 'status',
                  preset: f.key || 'all',
                })
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="chip-row">
          {TASK_DUE_FILTERS.map((f) => (
            <button
              key={f.key || 'any'}
              type="button"
              className={`chip ${due === f.key ? 'chip--active' : ''}`}
              onClick={() => {
                setDue(f.key)
                track(telemetryEvents.FILTER_APPLIED, {
                  screen: 'tasks',
                  filter_key: 'due',
                  preset: f.key || 'any',
                })
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {scopes.length > 1 && (
          <div className="chip-row toolbar__end">
            {scopes.map((s) => (
              <button
                key={s.key}
                type="button"
                className={`chip ${scope === s.key ? 'chip--active' : ''}`}
                onClick={() => setScope(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <div className="table-wrap">
          <div className="p-page flex-col gap-sm">
            <span className="skeleton table__skeleton-row" />
            <span className="skeleton table__skeleton-row" />
            <span className="skeleton table__skeleton-row" />
            <span className="skeleton table__skeleton-row" />
          </div>
        </div>
      )}

      {!loading && error && (
        <div className="state">
          <p className="state__title">{error}</p>
          <button type="button" className="btn btn--secondary" onClick={reload}>
            Повторить
          </button>
        </div>
      )}

      {!loading && !error && sorted.length === 0 && (
        <div className="state">
          <Icon name="tasks" size={32} />
          <p className="state__title">Задач не найдено</p>
          <p className="state__text">Измените фильтры или создайте новую задачу.</p>
          {due && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDue('')}>
              Сбросить фильтр по сроку
            </button>
          )}
        </div>
      )}

      {!loading && !error && sorted.length > 0 && (
        <div className="table-wrap">
          <table className="table table--dense">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th key={c.key} scope="col" className={`table__col table__col--${c.key}`}>
                    {c.sortable ? (
                      <button
                        type="button"
                        className="table__sort"
                        onClick={() => toggleSort(c.key)}
                        aria-sort={
                          sort.key === c.key
                            ? sort.dir === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        }
                      >
                        {c.label}
                        {sort.key === c.key && (
                          <Icon name={sort.dir === 'asc' ? 'chevronRight' : 'chevronLeft'} size={12} />
                        )}
                      </button>
                    ) : (
                      c.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => {
                const pr = priorityMeta(t.priority)
                const due_ = describeDue(t)
                return (
                  <tr
                    key={t.id}
                    className={`table__row--clickable ${due_?.isOverdue ? 'table__row--overdue' : ''}`}
                    tabIndex={0}
                    onClick={() => navigate(`/admin/tasks/${t.id}`)}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/admin/tasks/${t.id}`)}
                  >
                    <td className="table__num">{t.id}</td>
                    <td>
                      <span className="table__title">{t.title}</span>
                    </td>
                    <td>
                      <StatusBadge status={t.status} />
                    </td>
                    <td>
                      <span className={`badge badge--${pr.tone}`}>{pr.label}</span>
                    </td>
                    <td className="table__muted">{t.assignee?.full_name || '—'}</td>
                    <td className="table__muted">{t.car ? t.car.plate : '—'}</td>
                    <td>
                      {due_ ? (
                        <DueBadge due={due_} compact />
                      ) : (
                        <span className="table__muted">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
